import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { choosePreferredUserSnapshot } from '../../../functions/publicMetadataPolicy.js';
import { usePublicPageMetadata } from '../../hooks/usePublicPageMetadata';
import { NotFoundPage, PageUnavailable } from './PageStatus';

import PublicHeader from '../PublicHeader';
import MintNFTComponent from '../MintNFTComponent';
import ComponentAnalyticsTracker from '../ComponentAnalyticsTracker';
import '../MuultiStyle.css';

export default function MintNFTComponentDetailPage() {
    const { username, componentId } = useParams();
    const [muulti, setMuulti] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            setLoadError(false);
            try {
                const usersRef = collection(db, 'users');
                const q = query(usersRef, where('muultiName', '==', username));
                const snap = await getDocs(q);
                setMuulti(choosePreferredUserSnapshot(snap.docs)?.data() || undefined);
            } catch (e) {
                console.error(e);
                setLoadError(true);
                setMuulti(undefined);
            } finally {
                setLoading(false);
            }
        })();
    }, [username]);

    const comp = useMemo(() => {
        if (!muulti || !Array.isArray(muulti.components)) return null;
        return muulti.components.find(
            c => c?.type === 'mint' && String(c.id) === String(componentId)
        ) || null;
    }, [muulti, componentId]);

    usePublicPageMetadata(muulti, loading || loadError, Boolean(comp));

    if (loading) return <div className="muulti-container"><p>Loading¦</p></div>;
    if (loadError) return <PageUnavailable />;
    if (!muulti || !comp) return <NotFoundPage />;

    // --- Page background (match other detail pages)
    const template = muulti.template || '';
    const bg = muulti.background || { type: 'single', color: '#ffffff' };
    const pageStyle = (() => {
        if (bg.type === 'single') return { backgroundColor: bg.color || '#ffffff' };
        if (bg.type === 'gradient') {
            const colors = Array.isArray(bg.colors) && bg.colors.length >= 2 ? bg.colors : ['#ffffff', '#000000'];
            const direction = bg.direction || 'to right';
            return { backgroundImage: `linear-gradient(${direction}, ${colors[0]}, ${colors[1]})` };
        }
        if (bg.type === 'image') {
            return {
                backgroundColor:   bg.fallback || '#ffffff',
                backgroundImage:   bg.imageUrl ? `url(${bg.imageUrl})` : undefined,
                backgroundSize:    bg.sizing || 'cover',
                backgroundPosition:bg.position || 'center',
                backgroundRepeat:  bg.repeatMode || 'no-repeat',
            };
        }
        return {};
    })();

    // --- Header styling (match other detail pages)
    const hb = muulti.headerBackground || {
        type: 'color',
        color: '#ffffff',
        colors: ['#ffffff', '#000000'],
        fontColor: '#000000',
        direction: 'to right',
        imageUrl: '',
        sizing: 'cover',
        position: 'center',
    };
    const headerStyleProps = (() => {
        let base;
        switch (hb.type) {
            case 'color':
                base = { backgroundColor: hb.color };
                break;
            case 'gradient':
                base = { backgroundImage: `linear-gradient(${hb.direction}, ${hb.colors[0]}, ${hb.colors[1]})` };
                break;
            case 'image':
                base = {
                    backgroundImage: `url(${hb.imageUrl})`,
                    backgroundSize: hb.sizing,
                    backgroundPosition: hb.position,
                    backgroundRepeat: 'no-repeat'
                };
                break;
            default:
                base = {};
        }
        return {
            ...base,
            color: hb.fontColor,
            borderStyle: hb.borderStyle || 'none',
            borderWidth: hb.borderWidth ?? 1,
            borderColor: hb.borderColor || 'transparent',
            boxSizing: 'border-box',
            backgroundOrigin: 'border-box',
            backgroundClip: 'border-box'
        };
    })();

    // --- Container frame
    const containerStyle = {
        borderStyle:  muulti.containerFrame?.borderStyle,
        borderWidth:  muulti.containerFrame?.borderWidth ?? undefined,
        borderColor:  muulti.containerFrame?.borderColor,
        borderRadius: muulti.containerFrame?.borderRadius ?? undefined,
        paddingTop:    muulti.containerFrame?.paddingTop ?? undefined,
        paddingRight:  muulti.containerFrame?.paddingRight ?? undefined,
        paddingBottom: muulti.containerFrame?.paddingBottom ?? undefined,
        paddingLeft:   muulti.containerFrame?.paddingLeft ?? undefined,
        boxSizing: 'border-box',
    };

    return (
        <div className={`muulti-container ${template}`} style={{ ...pageStyle, width: '100%', ...containerStyle }}>
            <PublicHeader
                profilePicUrl={muulti.profilePicUrl}
                pageTitle={muulti.pageTitle || muulti.muultiName}
                pageDescription={muulti.pageDescription}
                alignment={muulti.alignment}
                headerPadding={muulti.headerPadding}
                style={headerStyleProps}
            />

            <ComponentAnalyticsTracker
                comp={comp}
                muulti={muulti}
                surface="detail"
                trackImpression={false}
                trackView
                className="public-components"
                style={{ gridTemplateColumns: 'repeat(1, minmax(280px, 1fr))', gridGap: '24px' }}
            >
                <MintNFTComponent comp={comp} style={comp.style || {}} pageTypography={muulti.typography || null} ownerPublicKey={muulti.publicKey || ''} />
            </ComponentAnalyticsTracker>

            <div className='detail-page-return-link-container'>
                <Link
                    className='muulti-button detail-page-return-link'
                    to={`/${username}`}
                >
                    † Back to {username}
                </Link>
            </div>
        </div>
    );
}
